import { IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import MapIcon from '@material-ui/icons/MapOutlined'
import SatelliteIcon from '@material-ui/icons/Satellite'
import StraightenIcon from '@mui/icons-material/Straighten'
import { ParentSize } from '@visx/responsive'
import classNames from 'classnames'
import React, { useEffect } from 'react'

import { AreaResponse, BestMileVehicle } from 'src/api/gen/api'
import useComponentControl from 'src/commons/hooks/useComponentControl'
import useSafeState from 'src/commons/hooks/useSafeState'
import { DrawLayer, GeoJSONLayer, MapActionBar, MapBoxMap, MapSpinner } from 'src/commons/map'
import { MAP_LAYERS } from 'src/commons/map/types'
import { MAP_VIEW_STORAGE } from 'src/commons/map/utils'
import { DEFAULT_BORDER_COLOR, DEFAULT_MAIN_COLOR } from 'src/components/ColorDot'

import MeasurementPanel from '../SupervisionMap/MeasurementPanel'

const SOURCE_NAME = 'devices'

const DISPLAY_LOGO_BREAKPOINT = 17

function useStyles() {
  return makeStyles({
    root: {
      flex: 1,
      height: '100%',
      borderTopRightRadius: 'inherit',
      borderBottomRightRadius: 'inherit',
    },
    mapSpinnerRoot: {
      paddingLeft: '240px', // Panel width
    },
  })()
}

export function deviceToProperties(vehicle: BestMileVehicle): GeoJSON.GeoJsonProperties {
  return {
    uuid: vehicle.id,
    nam: vehicle.name,
    capacity: vehicle.capacity,
    occupancy: vehicle.occupancy,
    lat: vehicle.latitude,
    lng: vehicle.longitude,
  }
}

function vehiclesToGeoJSON(
  vehicles: BestMileVehicle[],
  deviceToProperties: (vehicle: BestMileVehicle) => GeoJSON.GeoJsonProperties
): GeoJSON.FeatureCollection {
  const features = vehicles
    .filter((vehicle: BestMileVehicle) => vehicle.latitude && vehicle.longitude)
    .map(
      (vehicle: BestMileVehicle): GeoJSON.Feature<GeoJSON.Geometry> => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [vehicle.longitude, vehicle.latitude],
        },
        properties: deviceToProperties(vehicle),
      })
    )

  return {
    type: 'FeatureCollection',
    features,
  }
}

function addToMap(
  currentMap: mapboxgl.Map,
  assets: BestMileVehicle[],
  onMouseEnter: () => void,
  onMouseLeave: () => void,
  onLoaded?: () => void
) {
  const source = SOURCE_NAME
  const devicesId = MAP_LAYERS.DeviceLowZoom
  const devicesDetailsId = MAP_LAYERS.DeviceHighZoom
  const { sources = {} } = currentMap.getStyle()
  if (Object.keys(sources).find((key: string) => key === source)) {
    return
  }

  currentMap.addSource(source, {
    type: 'geojson',
    data: vehiclesToGeoJSON(assets, deviceToProperties),
  } as any)

  currentMap.addLayer({
    id: devicesId,
    source,
    type: 'circle',
    paint: {
      // make circles larger as the user zooms from z12 to z22
      'circle-radius': {
        base: 2,
        stops: [
          [1, 1],
          [14, 6],
          [15, 7],
          [DISPLAY_LOGO_BREAKPOINT, 14],
        ],
      },
      'circle-color': DEFAULT_MAIN_COLOR,
      'circle-stroke-color': DEFAULT_BORDER_COLOR,
      'circle-stroke-width': 0,
    } as any,
  })

  currentMap.on('mouseenter', devicesDetailsId, onMouseEnter)
  currentMap.on('mouseleave', devicesDetailsId, onMouseLeave)

  // When all loading operations are done, the map enters an idle state
  if (onLoaded) {
    currentMap.on('idle', onLoaded)
  }
}

function clearMap(currentMap: mapboxgl.Map) {
  // First we clear the map from the layers then the source
  const { layers = [], sources = {} } = currentMap.getStyle()
  layers.forEach(({ id }: any) => {
    if ((id === MAP_LAYERS.DeviceHighZoom || id === MAP_LAYERS.DeviceLowZoom) && currentMap.getLayer(id)) {
      currentMap.removeLayer(id)
    }
  })
  Object.keys(sources).forEach((key: string) => {
    if (key === SOURCE_NAME) {
      currentMap.removeSource(key)
    }
  })
}

interface IProps {
  areas: AreaResponse[]
  isLoading: boolean
  vehicles: BestMileVehicle[]
}

export default function VehiclesMap(props: IProps) {
  const { vehicles, isLoading } = props

  const [useSattelite, setUseSattelite] = useSafeState(false)

  const [mousePointer, setMousePointer] = useSafeState(false)
  const [measure, setMeasure] = useSafeState(false)
  const [map, setMap] = useSafeState<mapboxgl.Map | undefined>(undefined)
  const [mapInitialized, setMapInitialized] = useSafeState<boolean>(false)

  function setSatellite(value: boolean) {
    setUseSattelite(value)
    localStorage.setItem(MAP_VIEW_STORAGE, value ? '1' : '0')
  }

  function onMouseEnter() {
    setMousePointer(true)
  }

  function onMouseLeave() {
    setMousePointer(false)
  }

  useEffect(() => {
    if (map) {
      clearMap(map)
      addToMap(map, vehicles, onMouseEnter, onMouseLeave, () => setMapInitialized(true))
    }
  }, [map, vehicles])

  const componentControl = useComponentControl(
    <>
      <MapActionBar>
        <IconButton onClick={() => setSatellite(!useSattelite)}>
          {useSattelite ? <MapIcon /> : <SatelliteIcon />}
        </IconButton>
      </MapActionBar>
      <MapActionBar fitContent>
        <IconButton onClick={() => setMeasure(!measure)}>
          <StraightenIcon color={measure ? 'primary' : 'inherit'} />
        </IconButton>
      </MapActionBar>
    </>,
    [useSattelite, measure]
  )

  const classes = useStyles()
  return (
    <>
      <ParentSize
        className={classNames({ [classes.root]: true, forceMousePointer: mousePointer })}
        parentSizeStyles={{}}
      >
        {({ width, height }) => (
          <MapBoxMap
            width={width}
            height={height}
            options={{ center: [27.9165, 35.2921] }}
            onMapReady={(m) => setMap(m)}
            showSatellite={useSattelite}
            componentControl={componentControl}
          >
            {mapInitialized &&
              props.areas.map((area) => (
                <GeoJSONLayer
                  key={area.label}
                  id={area.label}
                  geoData={{
                    type: 'FeatureCollection',
                    features: area.polygons.map((polygon) => ({
                      type: 'Feature',
                      geometry: polygon.geom,
                      properties: { id: area.label },
                    })) as any,
                  }}
                  layerOptions={{
                    paint: { 'fill-color': area.color, 'fill-opacity': 0.15 },
                  }}
                />
              ))}
            <DrawLayer active={measure} mode="draw_line_string">
              <MeasurementPanel onCancel={() => setMeasure(false)} />
            </DrawLayer>
          </MapBoxMap>
        )}
      </ParentSize>
      <MapSpinner classes={{ root: classes.mapSpinnerRoot }} loading={!mapInitialized || isLoading} />
    </>
  )
}
